/**
 * ONE PASSPORT CONTROL PER EMPLOYEE — which views it offers, and where each goes.
 *
 * Oct 20 field test, Issue 05 / founder decision FD-08. Team Management used to
 * decide Passport links from the LEGACY `memberships.role` scalar
 * (`SERVICE_WRITER` → Advisor, `MANAGER` → Manager, nothing else). That scalar
 * is a compatibility projection that collapses a multi-role person to one value,
 * so technicians, owners, detail specialists and every mixed-role person got no
 * link at all.
 *
 * This module is the single, pure answer. It reads the person's AUTHORITATIVE
 * roles through `resolveMembershipRoles` (authority_role + work-role set; the
 * legacy scalar only where those are genuinely absent) and returns the views
 * one Passport control should offer. It decides nothing about access on its
 * own: the technician view is enabled only when the server
 * (`visible_technician_passports`: self, an Owner/Manager or Service Advisor of
 * a shop of current employment, an ORG_ADMIN of that shop's organisation —
 * Oct 20 addendum P1/P2 — or the recruiting basis) returned that person's
 * profile to this viewer, and the
 * Advisor / Manager views only when the viewer holds the authority those pages
 * themselves require (`useCanViewAdvisorStats` / `useCanViewManagerStats`:
 * OWNER or MANAGER via `isElevatedAuthority`).
 *
 * It creates nothing. A missing profile is reported as unavailable, never
 * "fixed" by creating a technician profile or a second identity (CORE-B: one
 * durable professional identity per person).
 *
 * VIEWS
 *   MANAGER          authority OWNER / MANAGER / ORG_ADMIN → /manager-passport/:userId
 *                    (Owners are shown the Manager view: there is no Owner
 *                    Passport page; FD-08 "an Owner … may access Manager and
 *                    Technician views").
 *   SERVICE_ADVISOR  work role SERVICE_ADVISOR → /advisor-passport/:userId
 *   TECHNICIAN       work role TECHNICIAN and/or DETAIL_SPECIALIST →
 *                    /technicians/:technicianProfileId, or /passport for the
 *                    viewer's own row. Detail Specialists have no separate
 *                    Passport page: their identity is the same
 *                    `technician_profiles` row (created at acceptance for
 *                    TECHNICIAN and DETAIL_SPECIALIST alike), so ONE option
 *                    covers both and is labelled for what the person does.
 *   DISPATCHER / PARTS have no Passport surface (see workLens.ts) and add no view.
 */
import { resolveMembershipRoles, type AuthorityRole, type MembershipRoleInput } from '@/lib/roles/multiRole';
import { isElevatedAuthority } from '@/lib/roles/offeredRoles';

export type TeamPassportView = 'MANAGER' | 'SERVICE_ADVISOR' | 'TECHNICIAN';

export interface TeamPassportOption {
  readonly view: TeamPassportView;
  readonly label: string;
  /** Where the option goes. `null` exactly when the option is unavailable. */
  readonly href: string | null;
  /** Why the option is unavailable to this viewer. `null` when it is available. */
  readonly unavailableReason: string | null;
}

export interface TeamPassportInput {
  /** The member whose Passport is being opened. */
  readonly member: MembershipRoleInput & { readonly user_id: string };
  /** The signed-in viewer. */
  readonly viewerUserId: string | null | undefined;
  /** The viewer's resolved authority in this shop. */
  readonly viewerAuthority: AuthorityRole;
  /**
   * The member's `technician_profiles.id` IF the server returned it to this
   * viewer (`visible_technician_passports` batch read). `null` means not visible OR not existing —
   * the client cannot tell those apart and must not guess.
   */
  readonly visibleTechnicianProfileId: string | null | undefined;
  /** Feature flags for the role-intelligence pages. */
  readonly flags: { readonly advisorPassports: boolean; readonly managerPassports: boolean };
}

/** Authorities that read as a Manager Passport. */
const MANAGER_VIEW_AUTHORITIES: readonly AuthorityRole[] = ['OWNER', 'MANAGER', 'ORG_ADMIN'];

export const TEAM_PASSPORT_REASONS = {
  roleIntelNotPermitted:
    'Only an Owner or Manager of this shop can open this Passport view.',
  technicianNotVisible:
    'No Technician Passport is visible to you for this person: they may have no current employment record at this shop, or no profile yet. Nothing is created from here.',
  noApplicableView:
    'No Passport view applies to this person’s roles (Dispatcher and Parts have no Passport view).',
} as const;

export function resolveTeamPassportOptions(input: TeamPassportInput): TeamPassportOption[] {
  const { member, viewerUserId, viewerAuthority, visibleTechnicianProfileId, flags } = input;
  const { authority, workRoles } = resolveMembershipRoles(member);
  const isSelf = !!viewerUserId && viewerUserId === member.user_id;
  const viewerMayOpenRoleIntel = isElevatedAuthority(viewerAuthority);
  const options: TeamPassportOption[] = [];

  if (flags.managerPassports && MANAGER_VIEW_AUTHORITIES.includes(authority)) {
    options.push(roleIntelOption('MANAGER', 'Manager view', `/manager-passport/${member.user_id}`, viewerMayOpenRoleIntel));
  }

  if (flags.advisorPassports && workRoles.includes('SERVICE_ADVISOR')) {
    options.push(roleIntelOption('SERVICE_ADVISOR', 'Service Advisor view', `/advisor-passport/${member.user_id}`, viewerMayOpenRoleIntel));
  }

  const tech = workRoles.includes('TECHNICIAN');
  const detail = workRoles.includes('DETAIL_SPECIALIST');
  if (tech || detail) {
    const label = tech && detail ? 'Technician / Detail Specialist view'
      : tech ? 'Technician view' : 'Detail Specialist view';
    if (isSelf) {
      // The viewer's own Passport: the self route, which reads their own
      // identity (and offers the work-lens switch between capacities).
      options.push({ view: 'TECHNICIAN', label, href: '/passport', unavailableReason: null });
    } else if (visibleTechnicianProfileId) {
      options.push({ view: 'TECHNICIAN', label, href: `/technicians/${visibleTechnicianProfileId}`, unavailableReason: null });
    } else {
      options.push({ view: 'TECHNICIAN', label, href: null, unavailableReason: TEAM_PASSPORT_REASONS.technicianNotVisible });
    }
  }

  return options;
}

function roleIntelOption(view: TeamPassportView, label: string, href: string, permitted: boolean): TeamPassportOption {
  return permitted
    ? { view, label, href, unavailableReason: null }
    : { view, label, href: null, unavailableReason: TEAM_PASSPORT_REASONS.roleIntelNotPermitted };
}

/**
 * The Technician Passport target for a ROSTER row (Shop Profile, Technicians
 * tab). Shared rule with the dayboard header link: the viewer's own row opens
 * `/passport`; anyone else opens THEIR profile or nothing. It never falls back
 * to the viewer's own Passport for somebody else (Oct 20 audit A1).
 */
export function resolveRosterTechnicianPassport(input: {
  /** `technicians.technician_profile_id` on the roster row. */
  readonly rosterProfileId: string | null | undefined;
  /** `technician_users.user_id` bound to the roster row, if any. */
  readonly linkedUserId: string | null | undefined;
  /** Profile ids the server returned to this viewer (RLS-filtered). */
  readonly visibleProfileIds: ReadonlySet<string>;
  /** Visible profile id for the linked user (RLS-filtered), if any. */
  readonly visibleProfileIdForLinkedUser: string | null | undefined;
  readonly viewerUserId: string | null | undefined;
}): { href: string | null; unavailableReason: string | null } {
  const { rosterProfileId, linkedUserId, visibleProfileIds, visibleProfileIdForLinkedUser, viewerUserId } = input;
  if (linkedUserId && viewerUserId && linkedUserId === viewerUserId) {
    return { href: '/passport', unavailableReason: null };
  }
  if (rosterProfileId && visibleProfileIds.has(rosterProfileId)) {
    return { href: `/technicians/${rosterProfileId}`, unavailableReason: null };
  }
  if (visibleProfileIdForLinkedUser) {
    return { href: `/technicians/${visibleProfileIdForLinkedUser}`, unavailableReason: null };
  }
  return { href: null, unavailableReason: TEAM_PASSPORT_REASONS.technicianNotVisible };
}
